"use client"
import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"

type AgentRow = {
  id: string
  userId: string
  totalSales: number
  responseRate: number
  avgResponseHours: number | null
  eacLicense: string | null
  photoUrl: string | null
  bio: string | null
  specialisations: string[]
  user: { id: string; name: string | null; phone: string | null; image: string | null }
  agency: { name: string } | null
  reviews: { rating: number }[]
}

type SortKey = "sales" | "rating" | "response"

function avgRating(reviews: { rating: number }[]): number {
  if (!reviews.length) return 0
  return reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
}

function StarRow({ rating }: { rating: number }) {
  const filled = Math.round(rating)
  return (
    <span style={{ letterSpacing: 1, fontSize: 13, color: "var(--gold-deep)" }}>
      {"★".repeat(filled)}
      <span style={{ color: "var(--line)" }}>{"★".repeat(5 - filled)}</span>
    </span>
  )
}

function AgentAvatar({ agent }: { agent: AgentRow }) {
  const src = agent.photoUrl ?? agent.user.image
  const initials = (agent.user.name ?? "A").substring(0, 2).toUpperCase()
  if (src) return <Image src={src} alt="" width={52} height={52} style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
  return (
    <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,var(--navy),var(--navy-soft))", display: "grid", placeItems: "center", fontSize: 16, fontWeight: 700, color: "var(--cream)", flexShrink: 0 }}>
      {initials}
    </div>
  )
}

export default function LeaderboardPage() {
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState<SortKey>("sales")

  useEffect(() => {
    fetch("/api/agents")
      .then(r => r.json())
      .then(d => { if (d.success) setAgents(d.data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  const sorted = [...agents].sort((a, b) => {
    if (sort === "sales") return b.totalSales - a.totalSales
    if (sort === "rating") return avgRating(b.reviews) - avgRating(a.reviews)
    if (sort === "response") {
      const aH = a.avgResponseHours ?? 9999
      const bH = b.avgResponseHours ?? 9999
      return aH - bH
    }
    return 0
  }).slice(0, 30)

  const sortTabs: { key: SortKey; label: string }[] = [
    { key: "sales", label: "Most Sales" },
    { key: "rating", label: "Highest Rated" },
    { key: "response", label: "Fastest Response" },
  ]

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <div style={{ background: "var(--navy)", padding: "64px 24px 56px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>Agent Leaderboard</div>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 44, fontWeight: 700, color: "var(--cream)", marginBottom: 14, lineHeight: 1.1 }}>
            Top-performing <em>agents in Zimbabwe</em>
          </h1>
          <p style={{ color: "rgba(253,251,247,0.75)", fontSize: 16, lineHeight: 1.7, maxWidth: 560 }}>
            Ranked by sales, rating, and response speed. Every agent on Zim Estates is EAC-licensed and KYC-verified.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "48px 24px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 36, flexWrap: "wrap" }}>
          {sortTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setSort(tab.key)}
              style={{ padding: "9px 20px", background: sort === tab.key ? "var(--navy)" : "var(--cream)", color: sort === tab.key ? "var(--cream)" : "var(--navy)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[0,1,2,3,4].map(i => (
              <div key={i} style={{ height: 88, background: "var(--cream-warm)", borderRadius: 14, animation: "shimmer 1.5s infinite", animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        ) : !sorted.length ? (
          <div style={{ textAlign: "center", padding: "64px 24px", color: "var(--muted)" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>-</div>
            <div style={{ fontSize: 15 }}>No agents yet.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {sorted.map((agent, idx) => {
              const rating = avgRating(agent.reviews)
              const rankColors = ["var(--gold)", "rgba(180,180,180,1)", "rgba(176,141,87,1)"]
              const isTop3 = idx < 3
              return (
                <div
                  key={agent.id}
                  style={{ background: "var(--cream)", border: `1px solid ${isTop3 ? "rgba(200,150,62,0.3)" : "var(--line)"}`, borderRadius: 14, padding: "18px 22px", display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", transition: "box-shadow 0.15s" }}
                >
                  <div style={{ width: 36, fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: isTop3 ? rankColors[idx] : "rgba(27,45,84,0.18)", textAlign: "center", flexShrink: 0 }}>
                    {idx + 1}
                  </div>
                  <AgentAvatar agent={agent} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 15 }}>{agent.user.name ?? "Agent"}</div>
                      {agent.eacLicense && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: "rgba(61,139,95,0.1)", color: "var(--green-text)" }}>EAC Verified</span>
                      )}
                    </div>
                    {agent.agency && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{agent.agency.name}</div>}
                    {agent.reviews.length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                        <StarRow rating={rating} />
                        <span style={{ fontSize: 11, color: "var(--muted)" }}>{rating.toFixed(1)} ({agent.reviews.length})</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 24, flexShrink: 0, textAlign: "center" }}>
                    <div>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 700, color: "var(--navy)", lineHeight: 1 }}>{agent.totalSales}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>Sales</div>
                    </div>
                    <div>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 700, color: "var(--navy)", lineHeight: 1 }}>{Math.round(agent.responseRate)}%</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>Response</div>
                    </div>
                    {agent.avgResponseHours != null && (
                      <div>
                        <div style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 700, color: "var(--navy)", lineHeight: 1 }}>{agent.avgResponseHours < 1 ? "<1h" : `${Math.round(agent.avgResponseHours)}h`}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>Avg reply</div>
                      </div>
                    )}
                  </div>
                  <Link
                    href={`/listings?agentId=${agent.userId}`}
                    style={{ padding: "8px 16px", background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "var(--navy)", textDecoration: "none", flexShrink: 0 }}
                  >
                    View listings
                  </Link>
                </div>
              )
            })}
          </div>
        )}

        <div style={{ marginTop: 48, padding: "28px 32px", background: "linear-gradient(135deg, var(--navy), #243660)", borderRadius: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--cream)", marginBottom: 6 }}>Are you an agent?</div>
            <div style={{ fontSize: 13, color: "rgba(253,251,247,0.7)" }}>List properties and build your reputation on Zim Estates.</div>
          </div>
          <Link href="/list-property" style={{ padding: "10px 22px", background: "var(--gold)", color: "var(--navy)", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none", flexShrink: 0 }}>Get started</Link>
        </div>
      </div>
    </div>
  )
}
